import React from 'react';
import Link from 'next/link';
import { ComponentNode } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';
import { resolveCustomThemeHref } from '../utils/linkHelpers';
import { getNestedValue } from '../utils/dataBinding';
import { resolveStaticPageValue } from '../utils/resolveStaticPageData';
import { buildBreadcrumbCrumbs } from '../renderer';

interface TextComponentProps {
    component: ComponentNode;
    data?: any;
    context?: {
        store_slug?: string;
        slug_type?: string;
        category_slug?: string;
        promotion_slug?: string;
        out_link?: string;
        basePath?: string;
        params?: Record<string, any>;
        staticPageData?: any;
    };
}

/**
 * `breadcrumb_text` (single-node placeholder) is the OLD trail shape, kept
 * working for any already-published page JSON that still has it saved —
 * newer pages built by the theme builder now emit a `breadcrumbCrumbs`
 * CONTAINER instead (see ContainerComponent.tsx), which renders each crumb as
 * its own individually stylable node. Both read the same shared
 * `buildBreadcrumbCrumbs` (packages/template-renderer/resolve.ts, vendored
 * into ../renderer) so the trail-computation logic itself is never
 * duplicated, only how the crumbs get wrapped into markup.
 */
const TextComponent: React.FC<TextComponentProps> = ({ component, data, context }) => {
    const { data: componentData, placeholder, styles, type, href, customClassName } = component;
    const inlineStyles = convertStylesToInline(styles);

    if (placeholder === 'breadcrumb_text') {
        const crumbs = buildBreadcrumbCrumbs(context);
        if (crumbs) {
            return (
                <p style={inlineStyles} className={customClassName}>
                    {crumbs.map((crumb, idx) => (
                        <React.Fragment key={`${crumb.label}-${idx}`}>
                            {idx > 0 && <span style={{ margin: '0 6px' }}>&gt;</span>}
                            {crumb.href ? (
                                <Link href={crumb.href} style={{ color: 'inherit', textDecoration: 'none' }}>
                                    {crumb.label}
                                </Link>
                            ) : (
                                <span>{crumb.label}</span>
                            )}
                        </React.Fragment>
                    ))}
                </p>
            );
        }
    }

    const dynamicValue = placeholder
        ? getNestedValue(data, placeholder) ?? resolveStaticPageValue(placeholder, context?.staticPageData)
        : undefined;
    const textValue =
        typeof dynamicValue === 'string'
            ? dynamicValue.trim() || componentData
            : dynamicValue ?? componentData;

    const resolvedHref = resolveCustomThemeHref(href, data, context);

    // page_description is a rich-text article (headings, paragraphs, lists) from the
    // footer-pages API, and disclaimer.disclaimer is get_disclaimer's affiliate-disclosure
    // paragraph — both arrive as HTML, not plain text, so both render as actual HTML
    // rather than a link or an escaped string.
    if (placeholder === 'page_description' || placeholder === 'disclaimer.disclaimer') {
        return (
            <div
                style={{
                    '--tw-prose-headings': 'var(--builder-heading-color, var(--theme-color-secondary, #222e48))',
                    ...inlineStyles,
                } as React.CSSProperties}
                className={`prose max-w-none ${customClassName || ''}`}
                dangerouslySetInnerHTML={{ __html: textValue || '' }}
            />
        );
    }

    if (type === 'TEXT') {
        if (resolvedHref) {
            return (
                <Link href={resolvedHref} style={inlineStyles} className={customClassName}>
                    {textValue}
                </Link>
            );
        }
        return (
            <span style={inlineStyles} className={customClassName || 'inline-block'}>
                {textValue}
            </span>
        );
    }

    if (resolvedHref) {
        return (
            <Link href={resolvedHref} style={inlineStyles} className={customClassName}>
                {textValue}
            </Link>
        );
    }

    return (
        <p style={inlineStyles} className={customClassName}>
            {textValue}
        </p>
    );
};

export default TextComponent;